import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormBuilder, FieldControl, Validators } from 'react-reactive-form';
import { editPenIcon, deleteIcon, dummyAvatar } from '../assets/superAdminAssets';
import { emailValidator } from '../helpers/validator.helper';
import { markAllControlsDirtyAndTouched } from '../helpers/form.helper';
import { useUser } from '../store/hooks/useUser';
import { CookieNames, getCookie } from '../helpers/cookie.helper';
import { useAppContext } from '../contexts/AppContext';
import { Constants } from '../constants';
import { getProfile, updateProfile } from '../services/profile.service';
import imageCompression from 'browser-image-compression';

const EditProfileTab = () => {
  const { user, setUser } = useUser();
  const { showLoader, hideLoader, showToaster, hideToaster } = useAppContext();
  const fileInputRef = useRef(null);
  const [profileImage, setProfileImage] = useState(null);

  const profileForm = useMemo(
    () =>
      FormBuilder.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, emailValidator]],
      }),
    []
  );

  useEffect(() => {
    getProfileData();
  }, []);

  async function getProfileData() {
    try {
      showLoader();
      const res = await getProfile(getCookie(CookieNames.USER_ID));
      if (res.status === 200) {
        setUser(res.resultObject);
        profileForm.patchValue({
          firstName: res.resultObject.firstName || '',
          lastName: res.resultObject.lastName || '',
          email: res.resultObject.email || '',
        });
        setProfileImage(res.resultObject.imagePath ? `data:image/png;base64,${res.resultObject.imagePath}` : null);
      }
    } catch (error) {
      console.log(error);
      showToaster("Something went wrong while fetching profile data. Please try again.", 'error');
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }

  const handleSubmit = async (e) => {
    try {
      showLoader();
      e.preventDefault();
      markAllControlsDirtyAndTouched(profileForm);

      if (profileForm.status === 'INVALID') {
        return;
      }

      let formData = new FormData();
      formData.append('Id', getCookie(CookieNames.USER_ID));
      formData.append('SessionId', getCookie(CookieNames.SESSION_ID));
      formData.append('Email', user.email);
      formData.append('FirstName', profileForm.value.firstName);
      formData.append('LastName', profileForm.value.lastName);

      if (profileImage) {
        const byteString = atob(profileImage.split(',')[1]);
        const mimeString = profileImage.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        formData.append('postedFile', blob, 'avatar.png');
      } else {
        formData.append('postedFile', '');
      }

      await updateProfile(getCookie(CookieNames.USER_ID), formData);
      showToaster("Profile updated successfully.", 'success');
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      await getProfileData();
    } catch (error) {
      console.log(error);
      showToaster("Something went wrong while updating profile. Please try again.", 'error');
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  };

  const handleEditClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageChange = async (event) => {
    try {
      const file = event.target?.files?.[0];
      const maxSize = 5 * 1024 * 1024;
      const allowedTypes = ['image/jpeg', 'image/png'];

      if (!file) return alert('No file selected');
      if (!allowedTypes.includes(file.type)) return alert('Please select a valid image file (JPEG or PNG).');
      if (file.size > maxSize) return alert('File size exceeds 5MB. Please choose a smaller file.');

      const options = {
        maxSizeMB: 0.5,         // Reduce max size to 0.5MB
        maxWidthOrHeight: 600,  // Reduce max dimensions to 600px
        useWebWorker: true,     // Keep WebWorker for performance
        initialQuality: 0.6     // Adjust image quality (lower = smaller size)
      };

      const compressedFile = await imageCompression(file, options);

      const reader = new FileReader();
      reader.readAsDataURL(compressedFile);
      reader.onloadend = () => {
        const imageData = reader.result;
        setProfileImage(imageData);
      };

      event.target.value = null;
    } catch (error) {
      console.error("Image compression failed:", error);
      showToaster("Something went wrong while updating profile. Please try again.", 'error');
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="lm-card edit-profile-box">
      <h3 className="lm-card-heading">Edit Profile</h3>
      <div className="setting-profile-sec">
        <div className="d-flex">
          <div className="profile-img">
            <img src={profileImage || dummyAvatar} alt="Profile" />
            <input
              type="file"
              id="profileImageInput"
              style={{ display: 'none' }}
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImageChange}
            />
            <button type="button" className="delete-btn" onClick={handleRemoveImage}>
              <img className="delete" src={deleteIcon} alt="Delete" />
            </button>
            <button type="button" className="edit-btn" onClick={handleEditClick}>
              <img className="edit" src={editPenIcon} alt="Edit" />
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row mt-4">
          <div className="col-md-4 col-lg-3">
            <FieldControl
              strict={false}
              render={({ handler, touched, hasError }) => (
                <div className={`form-group ${touched && hasError('required') ? 'error-field' : ''}`}>
                  <label htmlFor="firstName">
                    First Name
                    <span className="asterisk">*</span>
                  </label>
                  <input
                    {...handler()}
                    type="text"
                    className="form-control"
                    id="firstName"
                    placeholder="First Name"
                    maxLength={30}
                  />
                  {touched && hasError('required') && (
                    <span className="error-message">First name is required</span>
                  )}
                </div>
              )}
              control={profileForm.get('firstName')}
            />
          </div>

          <div className="col-md-4 col-lg-3">
            <FieldControl
              strict={false}
              render={({ handler, touched, hasError }) => (
                <div className={`form-group ${touched && hasError('required') ? 'error-field' : ''}`}>
                  <label htmlFor="lastName">
                    Last Name
                    <span className="asterisk">*</span>
                  </label>
                  <input
                    {...handler()}
                    type="text"
                    className="form-control"
                    id="lastName"
                    placeholder="Last Name"
                    maxLength={30}
                  />
                  {touched && hasError('required') && (
                    <span className="error-message">Last name is required</span>
                  )}
                </div>
              )}
              control={profileForm.get('lastName')}
            />
          </div>

          <div className="col-md-4 col-lg-3">
            <FieldControl
              strict={false}
              render={({ handler, touched, hasError }) => (
                <div
                  className={`form-group ${touched && (hasError('required') || hasError('invalidEmail')) ? 'error-field' : ''
                    }`}
                >
                  <label htmlFor="emailId">
                    Email ID
                    <span className="asterisk">*</span>
                  </label>
                  <input
                    {...handler()}
                    type="email"
                    className="form-control"
                    id="emailId"
                    placeholder="Email ID"
                    maxLength={30}
                    disabled
                  />
                  {touched && hasError('required') && (
                    <span className="error-message">Email is required</span>
                  )}
                  {touched && hasError('invalidEmail') && (
                    <span className="error-message">Please enter a valid email address</span>
                  )}
                </div>
              )}
              control={profileForm.get('email')}
            />
          </div>

          <div className="col-12">
            <button type="submit" className="lm-button1">
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditProfileTab;

